// Columna central de «Teléfonos»: píldoras, toolbar con métricas y paginación, y la DataTable
// compartida con una fila por modelo que se despliega en sus variantes.
import { useMemo } from 'react';
import { Badge, ErrorState } from '@/kit';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { StatsBar, type StatsBarItem } from '@/shared/patterns/StatsBar';
import { TableToolbar, ToolbarMetric } from '@/shared/patterns/TableToolbar';
import { TablePager, useTablePager } from '@/shared/patterns/TablePager';
import { formatMXN } from '@/shared/lib/format/money';
import { productTierLabel } from '@/shared/lib/format/productTier';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { isSoldOut } from '@/domains/sales/wizard/catalogAvailability';
import {
    modelLabel,
    productVariant,
    stockLabelOf,
    stockTone,
    stockToneOf,
    type StockTone,
} from '../../catalog/lib/catalogExplorer';
import {
    variantCountLabel,
    catalogEntries,
    modelTableRows,
    modelsAndVariantsLabel,
    priceRangeLabel,
    rowSku,
    SOME_VARIANTS_LABEL,
    variantCoverage,
    type ModelTableRow,
} from '../../catalog/lib/modelGroups';
import {
    groupFinanciers,
    ModelNameCell,
    sellLabel,
    VARIANT_ROW_CLASS,
    VariantNameCell,
} from '../../catalog/components/ModelRowCells';
import { useExpandedModels } from '../../catalog/hooks/useExpandedModels';
import {
    financierChips,
    nextPhoneSort,
    promotionLabel,
    type PhoneMetrics,
    type PhoneSortKey,
    type PhoneSortState,
    type PhoneStatFilter,
    type PhoneStats,
} from '../lib/phoneCatalog';

const DASH = '—';

export const STOCK_CLASS: Record<StockTone, string> = {
    ok: 'text-foreground',
    low: 'font-semibold text-warning',
    out: 'font-semibold text-danger',
};

type PhoneRow = ModelTableRow;

function FinancierBadges({ codes }: { codes: string[] }) {
    if (codes.length === 0)
        return <span className="text-[10px] text-muted-foreground">{DASH}</span>;
    return (
        <span className="flex gap-1">
            {financierChips(codes).map((chip) => (
                <Badge key={chip.code} variant={chip.tone} size="xs" title={chip.name}>
                    {chip.short}
                </Badge>
            ))}
        </span>
    );
}

function PromoBadge({ promo }: { promo: string | null }) {
    if (promo === null) return <span className="text-[10px] text-muted-foreground">{DASH}</span>;
    return (
        <Badge variant="info" size="xs" title={promo}>
            {promo}
        </Badge>
    );
}

/** La promo del modelo solo si aplica igual a todas sus variantes; si no, «En algunas variantes». */
function groupPromotion(row: PhoneRow): string | null {
    if (row.kind !== 'model') return promotionLabel(row.item);
    const coverage = variantCoverage(row.group, (item) => promotionLabel(item) ?? '');
    if (coverage.kind === 'some') return SOME_VARIANTS_LABEL;
    return coverage.kind === 'all' ? promotionLabel(coverage.sample) : null;
}

function phoneColumns({
    onToggle,
    onSell,
}: {
    onToggle: (key: string) => void;
    onSell: (sku: string) => void;
}): DataTableColumn<PhoneRow, PhoneSortKey>[] {
    return [
        {
            key: 'model',
            label: 'Modelo',
            // Anchos en %: suman 100 y caben en la columna central del explorer.
            width: '25%',
            sortKey: 'model',
            cell: (row) =>
                row.kind === 'model' ? (
                    <ModelNameCell
                        group={row.group}
                        expanded={row.expanded}
                        onToggle={() => {
                            onToggle(row.key);
                        }}
                    />
                ) : row.kind === 'variant' ? (
                    <VariantNameCell item={row.item} />
                ) : (
                    <>
                        {modelLabel(row.item) || DASH}
                        <span className="ml-1 text-[10px] text-muted-foreground">
                            {productVariant(row.item)}
                        </span>
                    </>
                ),
        },
        {
            key: 'variants',
            label: 'Variantes',
            width: '12%',
            cell: (row) => (row.kind === 'model' ? variantCountLabel(row.group) : ''),
            cellClassName: () => 'text-muted-foreground',
        },
        {
            key: 'tier',
            label: 'Gama',
            width: '10%',
            cell: (row) => (row.kind === 'variant' ? '' : productTierLabel(rowTier(row))),
            cellClassName: () => 'text-muted-foreground',
        },
        {
            key: 'price',
            label: 'Precio',
            width: '15%',
            numeric: true,
            sortKey: 'price',
            cellOverflow: 'nowrap',
            cell: (row) =>
                row.kind === 'model'
                    ? priceRangeLabel(row.group)
                    : formatMXN(row.item.cashPriceCents / 100),
        },
        {
            key: 'stock',
            label: 'Stock',
            width: '7%',
            numeric: true,
            sortKey: 'stock',
            cell: (row) =>
                row.kind === 'model'
                    ? row.group.soldOut
                        ? 'Agotado'
                        : String(row.group.stockTotal)
                    : stockLabelOf(row.item),
            cellClassName: (row) =>
                row.kind === 'model'
                    ? STOCK_CLASS[stockTone(row.group.stockTotal)]
                    : STOCK_CLASS[stockToneOf(row.item)],
        },
        {
            key: 'financiers',
            label: 'Financieras',
            width: '12%',
            cellOverflow: 'nowrap',
            cell: (row) => (
                <FinancierBadges
                    codes={
                        row.kind === 'model'
                            ? groupFinanciers(row.group)
                            : row.item.eligibleFinanciers
                    }
                />
            ),
        },
        {
            key: 'promo',
            label: 'Promoción',
            width: '11%',
            cell: (row) => <PromoBadge promo={groupPromotion(row)} />,
        },
        {
            key: 'action',
            label: <span className="sr-only">Vender</span>,
            width: '8%',
            cellOverflow: 'nowrap',
            cell: (row) =>
                row.kind === 'model' ? null : (
                    <div className="text-right">
                        <button
                            type="button"
                            disabled={isSoldOut(row.item)}
                            aria-label={sellLabel(row.item)}
                            onClick={(event) => {
                                event.stopPropagation();
                                onSell(row.item.sku);
                            }}
                            className="inline-flex items-center rounded-[5px] bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Vender
                        </button>
                    </div>
                ),
        },
    ];
}

function rowTier(row: PhoneRow): string {
    return row.kind === 'model' ? row.group.defaultVariant.tier : row.item.tier;
}

export function PhoneTable({
    rows,
    stats,
    metrics,
    stat,
    onStatChange,
    sort,
    onSortChange,
    selectedSku,
    onSelect,
    onSell,
    isLoading,
    isError,
    onRetry,
}: {
    rows: CatalogItem[];
    stats: PhoneStats;
    metrics: PhoneMetrics;
    stat: PhoneStatFilter;
    onStatChange: (next: PhoneStatFilter) => void;
    sort: PhoneSortState;
    onSortChange: (next: PhoneSortState) => void;
    selectedSku: string | null;
    onSelect: (sku: string) => void;
    /** «Vender» de una variante: arranca la venta con ese SKU. */
    onSell: (sku: string) => void;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
}) {
    const entries = useMemo(() => catalogEntries(rows), [rows]);
    const groups = useMemo(
        () => entries.flatMap((entry) => (entry.kind === 'model' ? [entry.group] : [])),
        [entries],
    );
    const pager = useTablePager(entries);
    const { expanded, toggle } = useExpandedModels(entries, selectedSku);
    const tableRows = modelTableRows(pager.pageRows, expanded);
    const columns = phoneColumns({ onToggle: toggle, onSell });

    if (isError) {
        return (
            <ErrorState
                className="m-3"
                title="No se pudo leer el catálogo de la tienda."
                description="Vuelve a intentarlo; si sigue igual, avísale a tu gerente."
                onRetry={onRetry}
            />
        );
    }

    const pills: StatsBarItem<PhoneStatFilter>[] = [
        { key: 'all', label: 'Todos', value: stats.all },
        { key: 'top', label: 'Top ventas', value: stats.top, tone: 'success' },
        { key: 'promo', label: 'Con promoción', value: stats.promo, tone: 'primary' },
        { key: 'low', label: 'Bajo stock', value: stats.low, tone: 'warning' },
        { key: 'out', label: 'Agotado', value: stats.out, tone: 'danger' },
    ];

    return (
        <>
            <StatsBar items={pills} active={stat} onChange={onStatChange} ariaLabel="Recortes" />

            <TableToolbar>
                <span aria-live="polite">
                    {isLoading ? `${DASH} modelos` : modelsAndVariantsLabel(groups)}
                </span>
                <ToolbarMetric
                    label="Precio prom"
                    value={
                        metrics.avgPriceCents === null
                            ? DASH
                            : formatMXN(metrics.avgPriceCents / 100)
                    }
                />
                <ToolbarMetric label="Stock total" value={metrics.stockTotal} />
                <ToolbarMetric
                    label="Con SK"
                    tone="success"
                    value={`${String(metrics.surekeepCount)} de ${String(metrics.count)}`}
                />
                <div className="flex-1" />
                <TablePager page={pager.page} pageCount={pager.pageCount} setPage={pager.setPage} />
            </TableToolbar>

            <DataTable
                columns={columns}
                rows={tableRows}
                minWidth="760px"
                rowKey={(row) => row.key}
                sort={sort}
                onSortToggle={(key) => {
                    onSortChange(nextPhoneSort(sort, key));
                }}
                onRowClick={(row) => {
                    if (row.kind !== 'model') {
                        onSelect(row.item.sku);
                        return;
                    }
                    if (!row.expanded) toggle(row.key);
                    if (!row.group.variants.some((variant) => variant.sku === selectedSku)) {
                        onSelect(row.group.defaultVariant.sku);
                    }
                }}
                rowClassName={(row) => (row.kind === 'variant' ? VARIANT_ROW_CLASS : undefined)}
                isRowSelected={(row) => rowSku(row) !== null && rowSku(row) === selectedSku}
                isLoading={isLoading}
                empty="Ningún producto coincide con los filtros."
                caption="Teléfonos de la tienda"
                density="compact"
            />
        </>
    );
}
